/**
 * TablePastePlugin - Limits pasted tables (from Excel, Word, etc.) to max 10 columns.
 * When clipboard HTML contains a table with more than 10 columns, we trim to the first 10
 * and show a warning to the user.
 */

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $generateNodesFromDOM } from '@lexical/html';
import {
  PASTE_COMMAND,
  COMMAND_PRIORITY_HIGH,
  $addUpdateTag,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  HISTORY_PUSH_TAG,
} from 'lexical';
import { useEffect } from 'react';
import { trimTablesToMaxColumns, MAX_TABLE_COLUMNS } from '../../utils/tablePasteUtils';

export default function TablePastePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const clipboardData = event?.clipboardData;
        if (!clipboardData) return false;

        const htmlString = clipboardData.getData('text/html');
        if (!htmlString) return false;

        const parser = new DOMParser();
        const dom = parser.parseFromString(htmlString, 'text/html');
        const tables = dom.querySelectorAll('table');
        if (tables.length === 0) return false;

        // Prevent browser default paste when we handle it - ensures history works
        if (event?.preventDefault) event.preventDefault();

        const trimmedCount = trimTablesToMaxColumns(dom);
        if (trimmedCount > 0) {
          // Show warning when we trimmed
          setTimeout(() => {
            alert(
              `Tables are limited to ${MAX_TABLE_COLUMNS} columns. ` +
                `Excess columns were removed from the pasted table${trimmedCount > 1 ? 's' : ''}.`
            );
          }, 100);
        }

        editor.update(() => {
          $addUpdateTag(HISTORY_PUSH_TAG); // Force history entry so undo/redo works for paste
          try {
            const nodes = $generateNodesFromDOM(editor, dom);
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.insertNodes(nodes);
            } else {
              $insertNodes(nodes);
            }
          } catch (err) {
            console.error('[TablePastePlugin] insert error:', err);
            return false;
          }
        });

        requestAnimationFrame(() => editor.getRootElement()?.focus());
        return true;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  return null;
}
